import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Ban, Loader2, Search, X } from "lucide-react";
import { arrayRemove, doc, serverTimestamp, updateDoc } from "firebase/firestore";
import SettingsLayout from "./SettingsLayout";
import useSettingsProfile from "./useSettingsProfile";
import useUserProfileList from "./useUserProfileList";
import { db } from "../../firebase";
import ProfileCard from "../../Components/ProfileCard";
import EmptyState from "../../Components/EmptyState";
import ConfirmDialog from "../../Components/ConfirmDialog";

export default function BlockedProfilesSettings() {
  const { user } = useSettingsProfile();
  const queryClient = useQueryClient();
  const { items, setItems, loading, error, setError } = useUserProfileList(user, "blocked");
  const [notice, setNotice] = useState("");
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = items.filter((p) => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return [p.fullName, p.profession, p.education, p.presentResidence, p.state]
      .filter(Boolean).join(" ").toLowerCase().includes(term);
  });

  const unblock = async () => {
    if (!user || !pending) return;
    setBusy(true); setNotice(""); setError("");
    try {
      await updateDoc(doc(db, "users", user.uid), {
        blocked: arrayRemove(pending.id),
        updatedAt: serverTimestamp(),
      });
      try {
        queryClient.setQueryData(["userDoc", user.uid], (currentDoc) => {
          const nextDoc = currentDoc || { blocked: [], shortlisted: [] };
          return {
            ...nextDoc,
            blocked: (nextDoc.blocked || []).filter((id) => id !== pending.id),
          };
        });
        queryClient.invalidateQueries({ queryKey: ["matches"] });
        queryClient.invalidateQueries({ queryKey: ["dashboardRecommendations"] });
      } catch {
        // ignore cache failures
      }
      setItems((c) => c.filter((it) => it.id !== pending.id));
      setNotice("Profile unblocked.");
      setPending(null);
    } catch (err) {
      setError(err.message || "Could not unblock profile.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <SettingsLayout
      title="Blocked profiles"
      description="Members you blocked will not appear in matches. Unblock anytime from this list."
    >
      {loading ? (
        <div className="card flex justify-center p-10">
          <Loader2 className="animate-spin text-brand-600" size={28} />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          Icon={Ban}
          tone="rose"
          title="No blocked profiles"
          description="Block from a profile page when you no longer want to see someone."
          action={<Link to="/matches" className="btn-primary px-5">Browse matches</Link>}
        />
      ) : (
        <>
          {/* Search */}
          <div className="mb-4 flex items-center gap-2">
            <div className="input-group flex-1 h-11">
              <Search size={16} className="text-ink-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, ID, profession..."
                className="text-[13px]"
              />
              {search && (
                <button onClick={() => setSearch("")} className="text-ink-400 hover:text-brand-600">
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {filtered.length === 0 ? (
            <EmptyState
              Icon={Search}
              title="No matches"
              description="Try a different search term."
            />
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
                {filtered.map((profile) => (
                  <ProfileCard
                    key={profile.id}
                    profile={profile}
                    variant="grid"
                    actions={(
                      <button
                        type="button"
                        onClick={() => setPending(profile)}
                        className="inline-flex w-full items-center justify-center gap-1.5 rounded-2xl border border-rose-200 bg-white px-3.5 py-2.5 text-[12.5px] font-semibold text-rose-600 transition hover:bg-rose-50"
                      >
                        Unblock
                      </button>
                    )}
                  />
                ))}
            </div>
          )}
        </>
      )}

      {notice && (
        <p className="mt-4 rounded-2xl bg-emerald-50 px-4 py-3 text-[13px] text-emerald-700">{notice}</p>
      )}
      {error && (
        <p className="mt-4 rounded-2xl bg-rose-50 px-4 py-3 text-[13px] text-rose-700">{error}</p>
      )}

      <ConfirmDialog
        open={!!pending}
        title="Unblock this profile?"
        description={pending ? `${pending.fullName} will be visible in your matches again.` : ""}
        Icon={Ban}
        tone="warning"
        confirmLabel="Yes, unblock"
        loading={busy}
        onClose={() => !busy && setPending(null)}
        onConfirm={unblock}
      />
    </SettingsLayout>
  );
}
